import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminRatesService } from '../services/adminRates';

const keys = {
  all: ['admin-rates'] as const,
  plans: () => [...keys.all, 'plans'] as const,
  planRates: (planId: string | number) => [...keys.all, 'planRates', planId] as const,
};

export const useAdminRates = () => {
  const queryClient = useQueryClient();

  const useAllPlansQuery = () => {
    return useQuery({
      queryKey: keys.plans(),
      queryFn: () => adminRatesService.getAllPlans(),
      staleTime: 5 * 60 * 1000,
    });
  };

  const usePlanRatesQuery = (planId: string | number, options?: { enabled?: boolean }) => {
    return useQuery({
      queryKey: keys.planRates(planId),
      queryFn: () => adminRatesService.getPlanRates(planId),
      enabled: !!planId && (options?.enabled ?? true),
      staleTime: 5 * 60 * 1000,
    });
  };

  const useCreatePlanMutation = () => {
    return useMutation({
      mutationFn: (payload: { name: string }) => adminRatesService.createPlan(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.plans() });
      },
    });
  };

  const useSaveAsNewPlanMutation = () => {
    return useMutation({
      mutationFn: (payload: { name: string, rates: any[] }) => adminRatesService.saveAsNewPlan(payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.plans() });
      },
    });
  };

  const useUpdatePlanRatesMutation = () => {
    return useMutation({
      mutationFn: ({ planId, payload }: { planId: string | number; payload: { name?: string, rates: any[] } }) =>
        adminRatesService.updatePlanRates(planId, payload),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.plans() });
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useUpdateRateMutation = () => {
    return useMutation({
      mutationFn: ({ planId, rateId, payload }: { planId: string | number; rateId: string | number; payload: any }) =>
        adminRatesService.updateRate(planId, rateId, payload),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useUploadRatesMutation = () => {
    return useMutation({
      mutationFn: ({ planName, file }: { planName: string; file: File }) =>
        adminRatesService.uploadRates(planName, file),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: keys.plans() });
      },
    });
  };

  const useUploadAndReplaceRatesMutation = () => {
    return useMutation({
      mutationFn: ({ planId, file }: { planId: string | number; file: File }) =>
        adminRatesService.uploadAndReplaceRates(planId, file),
      onSuccess: (_, { planId }) => {
        queryClient.invalidateQueries({ queryKey: keys.plans() });
        queryClient.invalidateQueries({ queryKey: keys.planRates(planId) });
      },
    });
  };

  const useChangeUserPlanMutation = () => {
    return useMutation({
      mutationFn: ({ userId, planId, planType }: { userId: string | number; planId: string | number; planType?: string }) =>
        adminRatesService.changeUserPlan(userId, planId, planType),
      onSuccess: (_, { userId }) => {
        queryClient.invalidateQueries({ queryKey: ['manage-users'] });
        queryClient.invalidateQueries({ queryKey: ['manage-users', 'detail', userId] });
      },
    });
  };

  const useChangeUserB2bPlanMutation = () => {
    return useMutation({
      mutationFn: ({ userId, b2bPlanId }: { userId: string | number; b2bPlanId: string | number }) =>
        adminRatesService.changeUserB2bPlan(userId, b2bPlanId),
      onSuccess: (_, { userId }) => {
        queryClient.invalidateQueries({ queryKey: ['manage-users'] });
        queryClient.invalidateQueries({ queryKey: ['manage-users', 'detail', userId] });
      },
    });
  };

  return {
    useAllPlansQuery,
    usePlanRatesQuery,
    useCreatePlanMutation,
    useSaveAsNewPlanMutation,
    useUpdatePlanRatesMutation,
    useUpdateRateMutation,
    useUploadRatesMutation,
    useUploadAndReplaceRatesMutation,
    useChangeUserPlanMutation,
    useChangeUserB2bPlanMutation,
    downloadSampleRates: adminRatesService.downloadSampleRates,
    downloadUserRates: adminRatesService.downloadUserRates,
  };
};
